import {Injectable} from '@angular/core';
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store";
import {SessionActions} from "../../../shared/actions";
import gql from "graphql-tag";
import {Observable} from "rxjs/Observable";
import {ApolloQueryResult} from "apollo-client";
import {
  AutoStocktakingDepotMutation,
  AutoStocktakingDepotMutationVariables,
  DeleteStocktakingMutation,
  FetchStocktakingsQuery,
  GenerateStocktakingChecklistPDFMutation,
  GenerateStocktakingReportPDFMutation,
  GetLatestStocktakingQuery,
  GetStocktakingQuery,
  GetStocktakingTypesQuery,
  InitDepotStocktakingMutation,
  InitMedicineStocktakingMutation,
  SaveStocktakingEntriesMutation,
  SaveStocktakingEntryMutation,
  SealStocktakingMutationMutation
} from "../../../shared/graphql/graphql.types";

export const stocktakingFragments = {
  stocktaking: gql`
    fragment StocktakingFields on Stocktaking {
      cuid label is_editable
      created_at updated_at reporting_date
      stocktaking_type {cuid label}
      is_sealed
      description notes
      administration_unit {cuid label }
      depot {cuid label}
      responsible_user {cuid label}
      involved_persons_names
    }
  `,
  stocktakingEntries: gql`
    fragment StocktakingEntryFields on StocktakingEntry {
      cuid label is_editable
      created_at updated_at stocktaking_timestamp
      pinned_down
      shall_amount is_amount
      product_batches {cuid label use_before_date batch_number}
      stock {
        cuid label
        depot_yard {cuid label}
        product_batches {cuid label use_before_date batch_number}
      }
    }
  `
};

export const getSotcktakingTypes = gql`
  query GetStocktakingTypes($label: String) {
    stocktakingTypes(label: $label) {
      cuid label is_editable
    }
  }
`;

export const initDepotStocktakingMutation = gql`
  mutation InitDepotStocktaking($depotCuid:String!, $stocktakingInput:StocktakingInput!) {
    createDepotStocktaking(depotCuid: $depotCuid, stocktakingInput:$stocktakingInput) {
      ...StocktakingFields
      stocktaking_entries {
        ...StocktakingEntryFields
      }
    }
  }
  ${stocktakingFragments.stocktaking}
  ${stocktakingFragments.stocktakingEntries}
`;

export const initMedicineStocktakingMutation = gql`
  mutation InitMedicineStocktaking($depotCuid:String!, $stocktakingInput:StocktakingInput!) {
    createMedicineStocktaking(depotCuid: $depotCuid, stocktakingInput:$stocktakingInput) {
      ...StocktakingFields
      stocktaking_entries {
        ...StocktakingEntryFields
      }
    }
  }
  ${stocktakingFragments.stocktaking}
  ${stocktakingFragments.stocktakingEntries}
`;

export const saveStocktakingEntryMutation = gql`
  mutation SaveStocktakingEntry($cuid:String!, $data:StocktakingEntryInput!) {
    saveStocktakingEntry(cuid:$cuid, data:$data) {
      ...StocktakingEntryFields
    }
  }
  ${stocktakingFragments.stocktakingEntries}
`;

export const saveStocktakingEntriesMutation = gql`
  mutation SaveStocktakingEntries($data:[StocktakingEntryInput]!) {
    saveStocktakingEntries(data:$data) {
      ...StocktakingEntryFields
    }
  }
  ${stocktakingFragments.stocktakingEntries}
`;

export const getStocktakingQuery = gql`
  query GetStocktaking($stocktakingCuid:String!, $onlyPinnedDown: Boolean, $notPinnedDown: Boolean) {
    stocktaking(cuid: $stocktakingCuid) {
      ...StocktakingFields
      stocktaking_entries(onlyPinnedDown: $onlyPinnedDown, notPinnedDown: $notPinnedDown) {
        ...StocktakingEntryFields
      }
    }
  }
  ${stocktakingFragments.stocktaking}
  ${stocktakingFragments.stocktakingEntries}
`;

export const getLatestStocktakingQuery = gql`
  query GetLatestStocktaking($adminUnitCuid:String!, $currentUserOnly:Boolean, $period:Int) {
    latestStocktaking(adminUnitCuid: $adminUnitCuid,
      currentUserOnly: $currentUserOnly, period:$period) {
      ...StocktakingFields
      stocktaking_entries {
        ...StocktakingEntryFields
      }
    }
  }
  ${stocktakingFragments.stocktaking}
  ${stocktakingFragments.stocktakingEntries}
`;

export const FetchStocktakings = gql`
  query FetchStocktakings($adminUnitCuid: String, $depotCuid: String, $onlyUnsealed: Boolean) {
    stocktakings(adminUnitCuid: $adminUnitCuid, depotCuid: $depotCuid, onlyUnsealed: $onlyUnsealed) {
      ...StocktakingFields
    }
  }
  ${stocktakingFragments.stocktaking}
`;

export const sealStocktakingMutation = gql`
  mutation SealStocktakingMutation($cuid: String!) {
    sealStocktaking(cuid: $cuid) {
      ...StocktakingFields
      stocktaking_entries {
        ...StocktakingEntryFields
      }
    }
  }
  ${stocktakingFragments.stocktaking}
  ${stocktakingFragments.stocktakingEntries}
`;

export const DeleteStocktaking = gql`
  mutation DeleteStocktaking($cuid: String!) {
    deleteStocktaking(cuid: $cuid)
  }
`;

export const generateStocktakingChecklistPDF = gql`
  mutation GenerateStocktakingChecklistPDF($cuid: String!) {
    stocktakingChecklistPdf(cuid: $cuid)
  }
`;

export const generateStocktakingReportPDF = gql`
  mutation GenerateStocktakingReportPDF($cuid: String!) {
    stocktakingReportPdf(cuid: $cuid)
  }
`;

export const startRfidStocktakingMutation = gql`
  mutation AutoStocktakingDepot($depotCuid: String!) {
    checkDepotByRfid(depotCuid: $depotCuid, autoSeal: false) {
      ...StocktakingFields
    }
  }
  ${stocktakingFragments.stocktaking}
`;

export interface StocktakingTypesResult extends GetStocktakingTypesQuery {
}

export interface CreateDepotStocktakingResult extends InitDepotStocktakingMutation {
}

export interface SaveStocktakingEntryResult extends SaveStocktakingEntryMutation {
}

export interface StocktakingResult extends GetStocktakingQuery {
}

export interface SealStocktakingMutationResult extends SealStocktakingMutationMutation {
}

@Injectable()
export class StocktakingService extends VisbosService {

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getStocktakingTypes(label?: string): ApolloQueryObservable<StocktakingTypesResult> {
    return this.fetchItems({
      query: getSotcktakingTypes,
      variables: {
        label
      }
    });
  }

  public initDepotStocktaking(depotCuid: string, stocktakingInput): Observable<ApolloQueryResult<CreateDepotStocktakingResult>> {
    return this.mutate<CreateDepotStocktakingResult>({
      mutation: initDepotStocktakingMutation,
      variables: {
        depotCuid,
        stocktakingInput
      },
      refetchQueries: ['FetchStocktakings', 'GetLatestStocktaking']
    })
  }

  public initMedicineStocktaking(depotCuid: string, stocktakingInput): Observable<ApolloQueryResult<InitMedicineStocktakingMutation>> {
    return this.mutate<InitMedicineStocktakingMutation>({
      mutation: initMedicineStocktakingMutation,
      variables: {
        depotCuid,
        stocktakingInput
      },
      refetchQueries: ['FetchStocktakings', 'GetLatestStocktaking']
    })
  }

  public saveStocktakingEntry(cuid: string, stocktakingEntryInput): Observable<ApolloQueryResult<SaveStocktakingEntryResult>> {
    return this.mutate<SaveStocktakingEntryResult>({
      mutation: saveStocktakingEntryMutation,
      variables: {
        cuid: cuid,
        data: this.processStocktakingEntryData(stocktakingEntryInput)
      }
    }, false)
  }

  public saveStocktakingEntries(stocktakingEntryInput: any[]): Observable<ApolloQueryResult<SaveStocktakingEntriesMutation>> {

    let data = stocktakingEntryInput.map(e => this.processStocktakingEntryData(e));

    return this.mutate({
      mutation: saveStocktakingEntriesMutation,
      variables: {
        data: data
      }
    });
  }

  public sealStocktaking(cuid: string): Observable<ApolloQueryResult<SealStocktakingMutationResult>> {
    return this.mutate<SealStocktakingMutationResult>({
      mutation: sealStocktakingMutation,
      variables: {
        cuid: cuid,
      },
      refetchQueries: ['GetLatestStocktaking']
    })
  }

  public getStocktaking(stocktakingCuid: string | Observable<string>): ApolloQueryObservable<StocktakingResult> {
    return this.fetchItems({
      query: getStocktakingQuery,
      variables: {
        stocktakingCuid: stocktakingCuid
      }
    })
  }

  public getLatestStocktaking(adminUnitCuid?: string | Observable<string>): ApolloQueryObservable<GetLatestStocktakingQuery> {
    return this.fetchItems({
      query: getLatestStocktakingQuery,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$
      },
      fetchPolicy: 'network-only'
    })
  }

  public fetchStocktakings(adminUnitCuid?: string | Observable<string>, depotCuid?: string | Observable<string>, onlyUnsealed?: boolean | Observable<boolean>): ApolloQueryObservable<FetchStocktakingsQuery> {
    return this.fetchItems({
      query: FetchStocktakings,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$,
        depotCuid: depotCuid,
        onlyUnsealed: onlyUnsealed
      }
    });
  }

  public processStocktakingEntryData(data) {
    let newData = JSON.parse(JSON.stringify(data));

    // remove deleted product batches and exclude delete marker from JSON output
    let clearedPbs = [];
    for (let pb of newData.product_batches || []) {
      if (!pb.deleted) {
        delete pb.deleted;
        clearedPbs.push(pb);
      }
      delete pb.deleted;
    }

    newData.product_batches = clearedPbs;

    return newData;
  }

  public deleteStocktaking(stocktakingCuid: string): Observable<ApolloQueryResult<DeleteStocktakingMutation>> {
    return this.mutate({
      mutation: DeleteStocktaking,
      variables: {
        cuid: stocktakingCuid
      },
      refetchQueries: ['FetchStocktakings', 'GetLatestStocktaking']
    })
  }

  public generateStocktakingReportPDF(stocktakingCuid: string): Observable<ApolloQueryResult<GenerateStocktakingReportPDFMutation>> {
    return this.mutate({
      mutation: generateStocktakingReportPDF,
      variables: {
        cuid: stocktakingCuid
      }
    });
  }

  public generateStocktakingChecklistPDF(stocktakingCuid: string): Observable<ApolloQueryResult<GenerateStocktakingChecklistPDFMutation>> {
    return this.mutate({
      mutation: generateStocktakingChecklistPDF,
      variables: {
        cuid: stocktakingCuid
      }
    });
  }

  public autoStocktakingForDepot(variables: AutoStocktakingDepotMutationVariables): Observable<ApolloQueryResult<AutoStocktakingDepotMutation>> {
    return this.mutate({
      mutation: startRfidStocktakingMutation,
      variables: variables,
      refetchQueries: ['FetchStocksForDepotQuery']
    })
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/stocktaking/stocktaking.service.ts